import { useEffect } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useAgentBranch, TESTING_ENVIRONMENTS, ALL_ENVIRONMENTS } from './utils';

/**
 * Environment selector for deploy-agent templates.
 *
 * This field controls WHICH ENVIRONMENT the agent is deployed to.
 *
 * - Main branch agents: all environments are available (dev, staging, prod, etc.)
 * - Feature branch agents: environment is locked to "dev" and the dropdown is disabled.
 *
 * Branch detection is handled by the shared useAgentBranch hook.
 *
 * Uses: `ui:field: ConditionalEnvironment` in template YAML.
 * Reads: `agent_ref` from sibling form fields via formContext.
 */
export const ConditionalEnvironmentField = ({
  onChange,
  rawErrors,
  formContext,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const formValues = formContext?.formData as Record<string, any> | undefined;
  const agentRef = formValues?.agent_ref as string | undefined;
  const currentValue = (formValues?.environment || '') as string;

  const { branch, isFeatureBranch, loading } = useAgentBranch(agentRef);

  const availableEnvironments = isFeatureBranch ? TESTING_ENVIRONMENTS : ALL_ENVIRONMENTS;

  // Auto-select dev when locked to feature branch
  useEffect(() => {
    if (isFeatureBranch && !TESTING_ENVIRONMENTS.includes(currentValue)) {
      onChange('dev');
    }
  }, [isFeatureBranch, currentValue, onChange]);

  const errorMessage = rawErrors?.[0];

  return (
    <FormControl
      margin="normal"
      error={rawErrors?.length > 0 && !rawErrors[0]?.startsWith('__no__error__')}
      fullWidth
    >
      <InputLabel htmlFor="environment" shrink>
        {schema.title || 'Environment'}
      </InputLabel>
      <Select
        id="environment"
        value={availableEnvironments.includes(currentValue) ? currentValue : ''}
        onChange={e => onChange(e.target.value as string)}
        disabled={loading}
      >
        {availableEnvironments.map(env => (
          <MenuItem key={env} value={env}>
            {env}
          </MenuItem>
        ))}
      </Select>
      {isFeatureBranch && (
        <FormHelperText>
          This agent is registered from branch "{branch}" — only dev and staging
          deployments are allowed until it is merged to main.
        </FormHelperText>
      )}
      {loading && (
        <FormHelperText>Loading agent metadata...</FormHelperText>
      )}
      {errorMessage && (
        <FormHelperText error>{errorMessage}</FormHelperText>
      )}
    </FormControl>
  );
};
